import React, { useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import AccountService from '../../api/account/account';
import { providers } from '../../api/account/factory';
import { useNotify, useQuery } from '../../hooks';
import { Login, LoginType } from '../../models/account';
import Config from '../../utils/config';
import CircularLoading from './../loading';
import styles from './callback.module.scss';
import { useGlobalContext } from '../../globalContext';
import {
    AGE_ANTISPOOFING_PATH,
    LOGIN_PATH,
    ROOT_PATH,
    VERIFY_ACCOUNT_PATH,
    FACE_MATCHING_PATH,
} from '../../utils/constants/Routes';
import { ALERT_TYPES } from '@yoti/react-components/alert';

const accountService = new AccountService(Config.apiUrl);

const allowedRedirectsURLs = [ROOT_PATH, AGE_ANTISPOOFING_PATH, FACE_MATCHING_PATH];

interface Props {
    onLogin?: () => void;
}

interface Params {
    provider: LoginType;
}

const Callback: React.FC<Props> = ({ onLogin }) => {
    const query = useQuery();
    const params = useParams<Params>();
    const history = useHistory();
    const notify = useNotify();

    const provider = params.provider;
    const callback = provider ? providers[provider].callback : undefined;
    const { account } = useGlobalContext();

    if (account) {
        history.replace(ROOT_PATH);
    }

    /**
     * Get the login token to request the external service in order to get the
     * user data. It returns undefined if the callback is not able to return the
     * information.
     */
    const getData = () => {
        try {
            return callback ? callback(query) : undefined;
        } catch {
            return undefined;
        }
    };
    const data = getData();

    const dataRef = JSON.stringify(data);
    useEffect(() => {
        // If user is already logged skip the rest of the code.
        if (account) return;
        // Take callback and check if it exists.
        if (!dataRef) {
            notify({
                message: 'Please try again, there were some invalid arguments in the url.',
                type: ALERT_TYPES.DANGER,
                icon: true,
            });
            history.replace(ROOT_PATH);
            return;
        }
        const data = JSON.parse(dataRef) as Login;

        // Run callback request to get account info.
        accountService
            .callback(data)
            .then((session) => {
                if (session.verified && !session.banned) {
                    if (onLogin) onLogin();
                    let redirect = data.redirect || ROOT_PATH;
                    if (
                        typeof redirect === 'object' &&
                        redirect !== null &&
                        'pathname' in redirect
                    ) {
                        if (!allowedRedirectsURLs.includes((redirect as any).pathname)) {
                            redirect = ROOT_PATH;
                        }
                    }
                    history.replace(redirect);
                } else {
                    history.replace({ pathname: VERIFY_ACCOUNT_PATH, state: { ...session } });
                }
            })
            .catch(() => {
                history.replace(LOGIN_PATH);
            });
    }, [dataRef, onLogin, history, account, notify]);

    return (
        <div className={styles['callback__loading-container']}>
            <CircularLoading />
        </div>
    );
};

export default Callback;
